import { AppModule } from '@app/app.module';
import { CalendarEventAction, CalendarEvent } from '@beego/ngx-calendar';
import { EventAttendanceType, EventLocationType } from '@internal-models/event.model';
import { TranslateService } from '@ngx-translate/core';
import { DateHelper } from '@shared-libs/helpers';
import { CalendarEventType, ICalendarEvent } from '@shared-models/calendar-event.model';
import { isAfter } from 'date-fns';

const translateService: TranslateService = AppModule.injector.get(TranslateService);

export class DisplayCalendarEventAdapter {
	public static fromCalendarEvent(
		calendarEvent: ICalendarEvent,
		actions: Array<CalendarEventAction>
	): CalendarEvent & { isInFuture?: boolean } {
		const startsAt = DateHelper.correctTimezone(calendarEvent.startsAt);
		const endsAt = DateHelper.correctTimezone(calendarEvent.endsAt);

		const isInFuture = isAfter(endsAt, new Date());

		const baseEvent: Partial<CalendarEvent & { isInFuture?: boolean }> = {
			start: startsAt,
			end: endsAt,
			actions: [],
			resizable: {
				beforeStart: false,
				afterEnd: false,
			},
			draggable: false,
			meta: { calendarEvent },
			isInFuture,
		};

		switch (calendarEvent.calendarEventType) {
			case CalendarEventType.InternalEvent:
				return {
					...baseEvent,
					actions: isInFuture ? actions : [],
					color: {
						primary: isInFuture ? calendarEvent.Event.color : calendarEvent.Event.color,
						secondary: isInFuture ? calendarEvent.Event.color : '#f1f1f1',
					},
					title: `<img class="_event-icon" src="./assets/icons/${
						calendarEvent.Event.attendanceType
					}-outline.svg" />${
						calendarEvent.Event.locationType === EventLocationType.Online
							? '<img class="_event-icon" src="./assets/icons/online-event-outline.svg" />'
							: ''
					}<b>${calendarEvent.Event.name}</b>${getDistance(calendarEvent)}<br>
					${calendarEvent.Event.DigibankAgency.name} - ${calendarEvent.Event.EventType.name}
					${getOccupation(calendarEvent)}`,
				} as CalendarEvent;
			case CalendarEventType.BeegoAppointment:
				const isBooked = calendarEvent.BeegoAppointmentId !== null;
				const isCanceled = calendarEvent.BeegoAppointment?.isCancelled;
				return {
					...baseEvent,
					color: {
						primary: isBooked ? '#edb142' : '#78BC61',
						secondary: isBooked ? '#edb142' : '#78BC61',
					},
					title: `<img class="_event-icon" src="./assets/icons/${isCanceled ? `cancel` : `beego`}-outline.svg" /><b>BEEGO student aan huis</b>${
						isBooked
							? `<br> Cliënt: ${calendarEvent.BeegoAppointment.Client.firstname} ${calendarEvent.BeegoAppointment.Client.lastname}`
							: ''
					}${getDistance(calendarEvent)}`,
				} as CalendarEvent;
			default:
				throw new Error(`Unsupported calendar event: ${calendarEvent}`);
		}
	}
}

const getDistance = (calendarEvent: ICalendarEvent): string =>
	`${calendarEvent.distance && !isNaN(calendarEvent.distance) ? `<br> ${calendarEvent.distance.toFixed(2)}km` : ''}`;

const getOccupation = (calendarEvent: ICalendarEvent): string => {
	switch (calendarEvent.Event.attendanceType) {
		case EventAttendanceType.WalkIn:
			return `<br>${translateService.instant('EVENT_ATTENDANCE_TYPE.WALKIN')}`;
		case EventAttendanceType.Reservation:
			return `<br>${translateService.instant('EVENT_ATTENDANCE_TYPE.RESERVATION')} - ${
				calendarEvent.reservations - calendarEvent.reserved
			}/${calendarEvent.reservations} ${translateService.instant('RESERVATION.FREE')}`;
		case EventAttendanceType.Registration:
			return `<br>${translateService.instant('EVENT_ATTENDANCE_TYPE.REGISTRATION')} - ${
				calendarEvent.Event.maxRegistrations - calendarEvent.reserved >= 0
					? calendarEvent.Event.maxRegistrations - calendarEvent.reserved
					: 0
			}/${calendarEvent.Event.maxRegistrations} ${translateService.instant('RESERVATION.FREE')}`;
		case EventAttendanceType.Lessons:
			return `<br>${translateService.instant('EVENT_ATTENDANCE_TYPE.LESSONS')} - ${
				calendarEvent.Event.maxRegistrations - calendarEvent.reserved
			}/${calendarEvent.Event.maxRegistrations} ${translateService.instant('RESERVATION.FREE')}`;
	}
};
